/**
 * Dropdown menu primitive: the kebab row menu, the column menu, the export menu and the
 * filter/sort/range popovers of the view header all hang off this.
 *
 * Purely presentational — it owns open/closed state and dismissal, nothing else. `children` is a
 * render function so items can close the menu after firing their action.
 */

import { useCallback, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useAnchoredPosition } from '../lib/anchored';
import { useDismissable } from '../lib/hooks';
import { cx } from './cx';

export type MenuProps = {
  /** Trigger content — text or an icon glyph. */
  label: ReactNode;
  /** Accessible name when `label` is a glyph. */
  ariaLabel?: string;
  children: (close: () => void) => ReactNode;
  align?: 'left' | 'right';
  triggerClassName?: string;
  panelClassName?: string;
  className?: string;
  disabled?: boolean;
  /** Rendered inside the trigger, after the label (e.g. an active-filter count badge). */
  badge?: ReactNode;
};

export function Menu({
  label,
  ariaLabel,
  children,
  align = 'left',
  triggerClassName = 'table-action',
  panelClassName,
  className,
  disabled = false,
  badge,
}: MenuProps) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // The panel is portaled, so it is outside `ref`'s subtree — it must be listed explicitly or
  // every click inside the menu would read as an outside press.
  const ref = useDismissable<HTMLDivElement>(open, close, [panelRef]);
  const panelStyle = useAnchoredPosition(open, triggerRef, panelRef, {
    align: align === 'right' ? 'end' : 'start',
    gapPx: 6,
  });

  return (
    <div className={cx('menu', className)} ref={ref} data-open={open ? 'true' : undefined}>
      <button
        type="button"
        ref={triggerRef}
        className={cx(triggerClassName, open && 'menu__trigger--open')}
        aria-label={ariaLabel}
        title={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        // Row kebabs fade in on row hover; this keeps an *open* one visible (`.row-menu`).
        data-open={open ? 'true' : undefined}
        disabled={disabled}
        onClick={() => setOpen((previous) => !previous)}
      >
        {label}
        {badge}
      </button>
      {open
        ? createPortal(
            <div
              className={cx('menu__panel', panelClassName)}
              ref={panelRef}
              style={panelStyle ?? undefined}
              role="menu"
            >
              {children(close)}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

export type MenuItemProps = {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  destructive?: boolean;
  className?: string;
  /** `menuitemradio` for single-selection groups (the scraping-mode block). */
  role?: 'menuitem' | 'menuitemradio' | 'menuitemcheckbox';
  /** Selection state; only meaningful for the two selectable roles. */
  checked?: boolean;
};

export function MenuItem({
  children,
  onClick,
  disabled,
  destructive,
  className,
  role = 'menuitem',
  checked,
}: MenuItemProps) {
  return (
    <button
      type="button"
      role={role}
      aria-checked={role === 'menuitem' ? undefined : checked === true}
      className={cx('menu__item', destructive && 'menu__item--danger', className)}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export function MenuSeparator() {
  return <div className="menu__sep" role="separator" />;
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <div className="menu__label">{children}</div>;
}

export default Menu;
